/**
 * Central SPA route policy — single source of truth for which client-side
 * routes (the react-router tree inside src/app.jsx) are public, which need an
 * authenticated session, and which follow the board-specific public/private
 * policy.
 *
 * Consumed by:
 *  - Components/UI/AuthGate (RequireAuth / BoardGate) — gate rendering.
 *  - app.jsx — decides where authenticated chrome (chat socket, global
 *    search, incoming call, floating badges, alarms) may mount.
 *  - axiosInstance — decides whether a dead session should redirect to "/".
 *
 * Default-deny: any path not listed here requires authentication.
 */

const PUBLIC_EXACT_PATHS = new Set([
  "/", // login
  "/register",
  "/pricing",
  "/rate-limit-error",
]);

// Path prefixes reachable without a session. Group invite links keep their
// own login flow (preview first, then /?redirect=... back here after login).
const PUBLIC_PATH_PREFIXES = [
  "/public/", // shared public resources, e.g. /public/task/:boardId/:taskId
  "/group/join/",
];

// /Board?ID=... has its own policy: public boards (PrivacyMood === "Public")
// are viewable anonymously; everything else requires a session + membership.
// NOTE: exact match only — /Board/pomodoro-popup/... stays protected.
const BOARD_PATH = "/board";

function normalize(pathname) {
  const path = String(pathname || "/").toLowerCase();
  return path.length > 1 ? path.replace(/\/+$/, "") : path;
}

export function isPublicPath(pathname) {
  const path = normalize(pathname);
  if (PUBLIC_EXACT_PATHS.has(path)) return true;
  return PUBLIC_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));
}

export function isBoardPath(pathname) {
  return normalize(pathname) === BOARD_PATH;
}

/** True when the path needs an authenticated session (board excluded — it has
 * its own gate that also handles public boards). */
export function requiresAuth(pathname) {
  return !isPublicPath(pathname) && !isBoardPath(pathname);
}
